import { useEffect, useRef } from "react";

import { useAuth } from "~/contexts/AuthProvider";
import { setWsCookie } from "~/helpers/setWsCookie";

import { useCable, useCableReady } from "../CableContext";
import {
  CableSubscriptionHandlers,
  CableSubscriptionParams,
  WebSocketConnectedArgs,
  WebSocketDisconnectedArgs,
  WebSocketMessage,
} from "../types/websocket";

export function useCableSubscription(
  channelName: string,
  params: CableSubscriptionParams,
  handlers: CableSubscriptionHandlers,
) {
  const cable = useCable();
  const cableReady = useCableReady();
  const handlerRef = useRef(handlers);
  const { token: authKey } = useAuth();
  const authKeyRef = useRef(authKey);

  useEffect(() => {
    handlerRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    authKeyRef.current = authKey;
  }, [authKey]);

  useEffect(() => {
    // The provider is still setting the WS auth cookie — subscribing now
    // would open the connection before the cookie exists. The effect
    // re-runs when `cableReady` flips.
    if (!cableReady) return;

    let hasRetried = false;
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let subscription: any = null;

    // Defer the subscribe by a tick so quick effect re-runs don't churn
    // subscribe/unsubscribe pairs against the server.
    //
    // Why: React Strict Mode (dev) double-invokes effects with a
    // mount → cleanup → mount cycle to surface bugs in cleanup logic. With
    // a synchronous subscribe, this produced subscribe → unsubscribe →
    // subscribe traffic on every mount, even though the user only opened
    // the channel once.
    //
    // With this defer, the first mount schedules a timer, the strict-mode
    // cleanup clears it via the `cancelled` flag before it ever fires, and
    // only the second mount's timer actually subscribes. On a real unmount
    // the cleanup either cancels a still-pending subscribe or unsubscribes
    // the active one — both branches are correct.
    //
    // Production unaffected: there's no double-mount, so the timer always
    // fires and subscribes once. The 0ms delay is invisible to the user.
    const timer = setTimeout(() => {
      if (cancelled) return;

      subscription = cable.subscriptions.create(
        { channel: channelName, ...params },
        {
          connected(args?: WebSocketConnectedArgs) {
            handlerRef.current.connected?.(args);
          },

          disconnected(arg?: WebSocketDisconnectedArgs) {
            const willAttemptReconnect = arg?.willAttemptReconnect ?? false;
            handlerRef.current.disconnected?.(willAttemptReconnect);

            // here 1 time we try to send a request to write another new cookie to use it for cable authentication later
            if (!willAttemptReconnect && !hasRetried) {
              hasRetried = true;

              void (async () => {
                try {
                  await setWsCookie(authKeyRef.current!);
                  cable.disconnect();
                  cable.connect();
                } catch (err) {
                  console.error("Failed to set WS-cookie:", err);
                }
              })();
            }
          },

          received(data: WebSocketMessage) {
            handlerRef.current.received?.(data);
          },
        },
      );
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      subscription?.unsubscribe();
    };
  }, [cable, cableReady, channelName, JSON.stringify(params)]);
}
